// First-touch attribution cookie. The contract (name, domain, value shape) is shared with
// the marketing site, which keeps its own copy of this logic: change both together.

const COOKIE_NAME = 'gumb_attr';
const STAGE_COOKIE_NAME = 'gumb_attr_stage';
const COOKIE_DOMAIN = '.gumb.app';
const COOKIE_MAX_AGE_SECONDS = 7776000; // 90 days
const COOKIE_VALUE_MAX = 200;
const EVENT_VALUE_MAX = 100;
const CONSENT_COOKIE_NAME = 'CookieScriptConsent';
// CookieScript category that maps to ad_storage (see googleconsentmap in its cookie).
const MARKETING_CATEGORY = 'targeting';

const CAMPAIGN_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;
const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid'] as const;

type ClickIdType = typeof CLICK_ID_KEYS[number] | 'none';

export type Attribution = {
  v: 1;
  ts: number;
  landing_path?: string;
  referrer_host?: string;
} & Partial<
  Record<typeof CAMPAIGN_KEYS[number] | typeof CLICK_ID_KEYS[number], string>
>;

export type AttributionEventParams = {
  first_utm_source?: string;
  first_utm_medium?: string;
  first_utm_campaign?: string;
  first_click_id_type?: ClickIdType;
  first_landing_path?: string;
};

const cookieName = (): string =>
  window.location.hostname.startsWith('stage.')
    ? STAGE_COOKIE_NAME
    : COOKIE_NAME;

const getCookie = (name: string): string | null => {
  const prefix = `${name}=`;
  const hit = document.cookie.split('; ').find((c) => c.startsWith(prefix));

  return hit ? hit.slice(prefix.length) : null;
};

const clip = (value: unknown, max: number): string =>
  typeof value === 'string' ? value.trim().slice(0, max) : '';

/**
 * True only after the visitor accepted the marketing category in the cookie banner.
 * Reads the CookieScript cookie directly: it exists before the banner script has loaded.
 */
export const hasMarketingConsent = (): boolean => {
  try {
    const raw = getCookie(CONSENT_COOKIE_NAME);

    if (!raw) return false;
    const consent = JSON.parse(decodeURIComponent(raw));
    const categories = JSON.parse(consent.categories || '[]');

    return (
      consent.action === 'accept' &&
      Array.isArray(categories) &&
      categories.includes(MARKETING_CATEGORY)
    );
  } catch {
    return false;
  }
};

/** Returns the parsed attribution cookie, or null. Never throws. */
export const readAttribution = (): Attribution | null => {
  try {
    if (typeof window === 'undefined') return null;
    const raw = getCookie(cookieName());

    if (!raw) return null;
    const parsed = JSON.parse(decodeURIComponent(raw));

    return parsed && typeof parsed === 'object' && parsed.v === 1
      ? (parsed as Attribution)
      : null;
  } catch {
    return null;
  }
};

const hasCampaignData = (attr: Attribution | null): boolean =>
  !!attr &&
  [...CAMPAIGN_KEYS, ...CLICK_ID_KEYS].some((key) => Boolean(attr[key]));

const buildFromLocation = (): Attribution => {
  const params = new URLSearchParams(window.location.search);
  const attr: Attribution = { v: 1, ts: Date.now() };

  [...CAMPAIGN_KEYS, ...CLICK_ID_KEYS].forEach((key) => {
    const value = clip(params.get(key), COOKIE_VALUE_MAX);

    if (value) attr[key] = value;
  });
  // Path only: the query string never leaves the URL.
  const landingPath = clip(window.location.pathname, COOKIE_VALUE_MAX);

  if (landingPath) attr.landing_path = landingPath;
  try {
    const host = document.referrer ? new URL(document.referrer).hostname : '';

    if (host) attr.referrer_host = clip(host, COOKIE_VALUE_MAX);
  } catch {
    // malformed referrer: leave it out
  }

  return attr;
};

/**
 * Writes the first-touch cookie when the visitor lands on the app with campaign data and
 * agreed to marketing cookies. An existing cookie is only replaced if it has no campaign data.
 */
export const captureAttribution = (): void => {
  try {
    if (typeof window === 'undefined' || !hasMarketingConsent()) return;
    const attr = buildFromLocation();

    if (!hasCampaignData(attr) || hasCampaignData(readAttribution())) return;
    document.cookie = [
      `${cookieName()}=${encodeURIComponent(JSON.stringify(attr))}`,
      `Domain=${COOKIE_DOMAIN}`,
      'Path=/',
      'Secure',
      'SameSite=Lax',
      `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
    ].join('; ');
  } catch {
    // attribution must never break the app
  }
};

/** Flat, truncated event keys; empty values are omitted. Raw click IDs are never sent. */
export const buildAttributionParams = (): AttributionEventParams => {
  try {
    const attr = hasMarketingConsent() ? readAttribution() : null;

    if (!attr) return {};
    const out: AttributionEventParams = {};
    const put = (
      key:
        | 'first_utm_source'
        | 'first_utm_medium'
        | 'first_utm_campaign'
        | 'first_landing_path',
      value: unknown,
    ) => {
      const clipped = clip(value, EVENT_VALUE_MAX);

      if (clipped) out[key] = clipped;
    };

    put('first_utm_source', attr.utm_source);
    put('first_utm_medium', attr.utm_medium);
    put('first_utm_campaign', attr.utm_campaign);
    out.first_click_id_type =
      CLICK_ID_KEYS.find((key) => Boolean(attr[key])) || 'none';
    put('first_landing_path', attr.landing_path);

    return out;
  } catch {
    return {};
  }
};
